import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient from '../../api/apiClient';
import LeaveHeader from '../../components/hris/leave/LeaveHeader';
import LeaveStats from '../../components/hris/leave/LeaveStats';
import LeaveTable from '../../components/hris/leave/LeaveTable';
import LeaveModal from '../../components/hris/leave/LeaveModal';
import { containerVariants, itemVariants } from '../../utils/animations';
import { useToast } from '../../context/ToastContext';

export default function LeaveList() {
  const { showSuccess, showError } = useToast();
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: '',
    leave_type: 'annual',
    start_date: '',
    end_date: '',
    reason: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await apiClient.get('/hris/leaves');
      setLeaves(response.data?.leaves || []);
      setEmployees(response.data?.employees || []);
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeName = empId => employees.find(e => e.id === empId)?.full_name || '-';

  const calculateDays = (start: string, end: string) => {
    if (!start || !end) return 0;
    const diff = new Date(end).getTime() - new Date(start).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const totalDays = calculateDays(formData.start_date, formData.end_date);
      await apiClient.post('/hris/leaves', {
        ...formData,
        total_days: totalDays,
        status: 'pending',
      });
      showSuccess('Pengajuan cuti berhasil dibuat');
      loadData();
      setDialogOpen(false);
      setFormData({
        employee_id: '',
        leave_type: 'annual',
        start_date: '',
        end_date: '',
        reason: '',
      });
    } catch (error) {
      showError('Gagal membuat pengajuan cuti');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async leave => {
    try {
      await apiClient.put(`/hris/leaves/${leave.id}`, {
        status: 'approved',
        approved_date: new Date().toISOString().split('T')[0],
      });
      showSuccess('Pengajuan cuti berhasil disetujui');
      loadData();
    } catch (error) {
      showError('Gagal menyetujui pengajuan cuti');
    }
  };

  const handleReject = async leave => {
    try {
      await apiClient.put(`/hris/leaves/${leave.id}`, { status: 'rejected' });
      showSuccess('Pengajuan cuti berhasil ditolak');
      loadData();
    } catch (error) {
      showError('Gagal menolak pengajuan cuti');
    }
  };

  const filteredLeaves = leaves.filter(leave =>
    filterStatus === 'all' ? true : leave.status === filterStatus
  );

  const stats = {
    pending: leaves.filter(l => l.status === 'pending').length,
    approved: leaves.filter(l => l.status === 'approved').length,
    rejected: leaves.filter(l => l.status === 'rejected').length,
  };

  return (
    <motion.div
      className="space-y-4 md:space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <LeaveHeader onAdd={() => setDialogOpen(true)} />
      </motion.div>
      <motion.div variants={itemVariants}>
        <LeaveStats stats={stats} />
      </motion.div>
      <motion.div variants={itemVariants}>
        <LeaveTable
          leaves={filteredLeaves}
          loading={loading}
          filterStatus={filterStatus}
          onFilterChange={setFilterStatus}
          getEmployeeName={getEmployeeName}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      </motion.div>
      <LeaveModal
        open={dialogOpen}
        form={formData}
        setForm={setFormData}
        employees={employees}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSave={handleSubmit}
      />
    </motion.div>
  );
}
